/* HeroGallery — preview index that lists all 6 hero design alternatives.
 *
 * Opens at `/preview/heroes`. Each card links to a route that mounts the
 * full landing page with that hero swapped in, so the user evaluates each
 * option in the real context of the rest of the site.
 */
import { Link } from "react-router-dom";
import { ArrowRight, Sparkles } from "lucide-react";

const OPTIONS = [
  {
    slug: "pattern-1",
    name: "Pattern 1",
    title: "Word-morph headline",
    sub: "Linear / Webflow style — one steady sentence, one word morphs every 2.5s",
    headline: "Wholesale, [paid in 24h | covered end-to-end | risk-checked | fully automated].",
    risk: "Low",
    bestFor: "Conveys breadth with focus. Best for product-led brands.",
    color: "bg-lime-100 border-lime-300",
  },
  {
    slug: "pattern-2",
    name: "Pattern 2",
    title: "Umbrella headline + 4-pillar strip",
    sub: "Stripe style — one big claim, 4 scannable cards below",
    headline: "Wholesale, fully managed.",
    risk: "Low",
    bestFor: "Most informational. Best when prospects skim.",
    color: "bg-sky-100 border-sky-300",
  },
  {
    slug: "pattern-3",
    name: "Pattern 3",
    title: "Static hero + rotating product visual",
    sub: "Ramp style — left side static, right side cycles through 4 product screens",
    headline: "Wholesale, fully managed.",
    risk: "Medium",
    bestFor: "Shows the product surfaces visually without burying the value prop.",
    color: "bg-amber-100 border-amber-300",
  },
  {
    slug: "pattern-4",
    name: "Pattern 4",
    title: "Bento grid as the hero",
    sub: "Notion / Linear style — skip the traditional hero, lead with a bento of all 4 superpowers",
    headline: "The OS for wholesale.",
    risk: "High (bold)",
    bestFor: "Elite visual signal. Risk: less direct for cold visitors.",
    color: "bg-violet-100 border-violet-300",
  },
  {
    slug: "pattern-5",
    name: "Pattern 5",
    title: "Icon-word morph",
    sub: "Mercury / Apple style — one word + icon morphs together (paid 💸 / covered 🛡 / tracked 📊 / automated ⚡)",
    headline: "Wholesale, but [paid | covered | tracked | automated].",
    risk: "Low",
    bestFor: "Playful + premium. Great for younger / design-led brands.",
    color: "bg-rose-100 border-rose-300",
  },
  {
    slug: "combo",
    name: "🥇 Recommended",
    title: "Combo · Pattern 1 + Pattern 2",
    sub: "Word-morph headline + 4-pillar strip below. The best of both: dynamic + scannable.",
    headline: "Wholesale, [paid in 24h | covered end-to-end | risk-checked | fully automated]. + 4-pillar strip",
    risk: "Low",
    bestFor: "Best overall conversion. Recommended pick.",
    color: "bg-gradient-to-br from-lime-200 to-sky-200 border-slate-900",
    starred: true,
  },
];

export default function HeroGallery() {
  return (
    <div className="min-h-screen bg-stone-50">
      {/* Header strip */}
      <div className="border-b border-stone-200 bg-white">
        <div className="max-w-[1280px] mx-auto px-6 md:px-12 py-6 flex items-center justify-between">
          <div>
            <div className="text-[10px] mono uppercase tracking-[0.22em] text-stone-500">verqo · design gallery</div>
            <h1 className="text-2xl font-semibold tracking-tight mt-0.5">Hero design alternatives</h1>
          </div>
          <Link to="/" className="text-[13px] text-stone-600 hover:text-stone-900 inline-flex items-center gap-1.5">
            ← Current live hero
          </Link>
        </div>
      </div>

      {/* Intro */}
      <div className="max-w-[1280px] mx-auto px-6 md:px-12 pt-8 pb-4">
        <p className="text-[15px] text-stone-700 leading-relaxed max-w-2xl">
          Six alternative hero designs, each rendered <strong>inside the full real landing page</strong>.
          Click any card to see how that hero would look on verqo.com with all the existing sections (industries, problem, solution, bento, pricing, etc.) below.
        </p>
        <p className="text-[12.5px] text-stone-500 mt-2">
          The current production hero (<em>"Stop waiting 60 days to get paid."</em>) stays untouched — these are read-only previews.
        </p>
      </div>

      {/* Grid */}
      <div className="max-w-[1280px] mx-auto px-6 md:px-12 pb-16 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {OPTIONS.map((opt) => (
          <Link
            key={opt.slug}
            to={`/preview/hero/${opt.slug}`}
            className={`group rounded-2xl border-2 ${opt.color} p-6 hover:shadow-[0_24px_60px_-24px_rgba(15,23,42,0.35)] hover:-translate-y-1 transition-all relative overflow-hidden`}
            data-testid={`gallery-${opt.slug}`}
          >
            {opt.starred && (
              <div className="absolute top-3 right-3 inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-slate-900 text-lime-300 text-[10px] font-semibold uppercase tracking-wider">
                <Sparkles className="w-3 h-3"/> My pick
              </div>
            )}
            <div className="text-[10.5px] mono uppercase tracking-[0.22em] text-stone-600">{opt.name}</div>
            <h3 className="mt-1.5 text-[19px] font-semibold tracking-tight text-stone-900 leading-tight">{opt.title}</h3>
            <p className="mt-2 text-[12.5px] text-stone-700 leading-relaxed">{opt.sub}</p>
            <div className="mt-4 rounded-lg bg-white/70 border border-white/80 p-3">
              <div className="text-[10px] uppercase tracking-wider text-stone-500 mb-1">Headline</div>
              <div className="text-[13px] font-medium text-stone-900 leading-snug">{opt.headline}</div>
            </div>
            <div className="mt-3 grid grid-cols-2 gap-2 text-[11px]">
              <div>
                <div className="text-stone-500 mono uppercase tracking-wider">Risk</div>
                <div className="text-stone-900 font-medium">{opt.risk}</div>
              </div>
              <div>
                <div className="text-stone-500 mono uppercase tracking-wider">Best for</div>
                <div className="text-stone-900 leading-tight">{opt.bestFor}</div>
              </div>
            </div>
            <div className="mt-5 inline-flex items-center gap-1.5 text-[12.5px] font-semibold text-slate-900 group-hover:gap-2.5 transition-all">
              See it on the real page <ArrowRight className="w-3.5 h-3.5"/>
            </div>
          </Link>
        ))}
      </div>

      {/* Sticky footer note */}
      <div className="border-t border-stone-200 bg-white">
        <div className="max-w-[1280px] mx-auto px-6 md:px-12 py-5 text-center text-[12.5px] text-stone-600">
          When you pick a winner, ping me and I'll wire it as the new production hero (read-only previews don't ship anything until you say so).
        </div>
      </div>
    </div>
  );
}
